import { useRef, useState } from 'react';
import { Receipt } from 'lucide-react';
import { DialogShell } from '../ui/DialogShell';
import { ModalHeader } from '../ui/ModalHeader';
import { FormErrorAlert } from '../ui/FormErrorAlert';
import { DatePicker } from '../ui/DatePicker';
import { ComprobanteInput } from './ComprobanteInput';
import { MedioPagoFolio } from './MedioPagoFolio';
import { useComprobanteFormulario } from '../../hooks/useComprobanteFormulario';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { comprobanteService } from '../../services/comprobanteService';
import type { Movimiento } from '../../lib/supabase';
import type { EntidadDinero } from '../../types/comprobante';
import { fechaEnChile } from '../../utils/dateHelpers';
import { formatCurrency } from '../../utils/formatters';
import { mensajeErrorDeNegocio } from '../../utils/erroresDeNegocio';
import { reportarErrorInesperado } from '../../utils/reportarErrorInesperado';

interface ReembolsoModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly entidad: EntidadDinero;
  readonly movimiento: Movimiento;
}

export const ReembolsoModal: React.FC<ReembolsoModalProps> = ({
  isOpen, onClose, onSuccess, entidad, movimiento,
}) => {
  const [guardando, setGuardando] = useState(false);
  // Día civil de Chile, no el del reloj del equipo: decide el mes contable del reembolso. Vacío si no
  // se puede determinar (falla cerrado: el formulario exige elegir la fecha).
  const [hoy] = useState(() => fechaEnChile(new Date()) ?? '');
  const [fecha, setFecha] = useState(hoy);
  const { error, clearError, handleError } = useErrorHandler();
  const comprobante = useComprobanteFormulario({
    entidad, fechaMovimiento: fecha, permiteSubirDespues: true, pideMedioPago: true, sentidoDinero: 'egreso',
  });
  const closeRef = useRef<(() => void) | null>(null);

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    if (!fecha) { handleError('Indica la fecha del reembolso.'); return; }
    const errorComprobante = comprobante.validar();
    if (errorComprobante) { handleError(errorComprobante); return; }
    try {
      setGuardando(true);
      const respaldo = await comprobante.resolver();
      await comprobanteService.marcarReembolsado(movimiento.id, { ...respaldo, fecha });
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const deNegocio = mensajeErrorDeNegocio(err);
      if (deNegocio) handleError(deNegocio);
      else reportarErrorInesperado(err, 'No pudimos marcar el reembolso. Intenta nuevamente.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <DialogShell
      isOpen={isOpen} onClose={onClose} size="md" level="nested"
      hasUnsavedChanges={guardando || comprobante.hayCambios} closeRef={closeRef}
      header={<ModalHeader title="Marcar como reembolsado" icon={<Receipt className="w-6 h-6 text-blue-600" />} onClose={() => closeRef.current?.()} />}
    >
      <form onSubmit={guardar} className="space-y-5">
        <FormErrorAlert error={error} onClear={clearError} />
        <ComprobanteInput
          comprobante={comprobante}
          titulo="Comprobante del reembolso"
          ayuda="Recibo del talonario firmado por quien recibe el dinero, o la captura de la transferencia."
          deshabilitado={guardando}
        />
        <p className="text-sm text-slate-600">
          Reembolso de {formatCurrency(movimiento.monto)} a {movimiento.reembolso_a_nombre_externo ?? 'el miembro'}
        </p>
        <DatePicker label="Fecha del reembolso" value={fecha} onChange={setFecha} required max={hoy || undefined} />
        <MedioPagoFolio comprobante={comprobante} deshabilitado={guardando} />
        <div className="flex gap-3 pt-4 border-t border-slate-200">
          <button type="button" onClick={() => closeRef.current?.()} disabled={guardando}
            className="flex-1 min-h-11 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 disabled:opacity-50">
            Cancelar
          </button>
          <button type="submit" disabled={guardando || comprobante.subiendo}
            className="flex-1 min-h-11 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {guardando ? 'Guardando…' : 'Marcar reembolsado'}
          </button>
        </div>
      </form>
    </DialogShell>
  );
};
